import React, {useEffect, useState} from "react"
import {AppProvider} from "./AppContext"
import {layoutMinResolution} from "./settings"
import {useCatalog} from "./Hooks/Catalog"
import {useHelpCta} from "./Hooks/HelpCta"
import {useHelp} from "./Hooks/Help"
import {useIsMobile} from "./Hooks/Mobil"
import {useLocal} from "./Hooks/Local"
import {useMessage} from "./Hooks/Message"
import {useModal} from "./Hooks/Modal"
import {useProgress} from "./Hooks/Progress"
import {useRoomParameters} from "./Hooks/RoomParameters"
import {useRoomSetup} from "./Hooks/RoomSetup"
import {useSolutionRepository} from "./Hooks/SolutionRepository"
import {useSolution} from "./Hooks/Solution"
import {BaseLayout} from "./Components/BaseLayout"
import {Canvas} from "./Components/Canvas"
import {Ceiling} from "./Views/Ceiling"
import {Conditions} from "./Views/Conditions"
import {Floor} from "./Views/Floor"
import {Help} from "./Modal/Help"
import {getCodeFromUrl, LoadRoom, LoadScreen} from "./Views/LoadScreen"
import {MenuBottom} from "./Components/MenuBottom"
import {MenuTopSimple} from "./Components/MenuTop"
import {MenuTop} from "./Components/MenuTop"
import {Message} from "./Components/Message"
import {Noise} from "./Views/Noise"
import {PrivacyInfo} from "./Modal/PrivacyInfo"
import {RoomType} from "./Views/RoomType"
import {Shape} from "./Views/Shape"
import {SmallScreenLook} from "./Views/SmallScreenLook"
import {SolutionProductAdd} from "./Modal/SolutionProductAdd"
import {SolutionProducts} from "./Modal/SolutionProducts"
import {SolutionRepositoryLoad, SolutionRepositorySave} from "./Modal/SolutionRepository"
import {SolutionReview} from "./Modal/SolutionReview"
import {AdditionalSolutionSuggestions} from "./Modal/AdditionalSolutionSuggestions"
import {SolutionSuggestions} from "./Modal/SolutionSuggestions"
import {Solution} from "./Views/Solution"
import {Wall} from "./Views/Wall"
import {Debug} from "./Components/Debug"
import {useUrlQueryComponent} from "./Hooks/UrlQueryComponent"
import {RequestQuote} from "./Modal/RequestQuote"
import {useLightbox} from "./Hooks/Lightbox"
import {Gallery} from "./Modal/Gallery"

export const App = () => {
    const catalog = useCatalog()
    const help = useHelp()
    const helpCta = useHelpCta()
    const mobil = useIsMobile(layoutMinResolution.width, layoutMinResolution.height)
    const modal = useModal()
    const roomParameters = useRoomParameters()
    const solutionRepository = useSolutionRepository()
    const local = useLocal()
    const message = useMessage()
    const roomSetup = useRoomSetup(local, message)
    const solution = useSolution(local, message)
    const progress = useProgress(roomSetup)
    const urlQueryComponent = useUrlQueryComponent()
    const lightbox = useLightbox()

    const [product, setProduct] = useState([]);

    const provider = {
        catalog,
        help,
        helpCta,
        message,
        modal,
        progress,
        roomParameters,
        roomSetup,
        solution,
        solutionRepository,
        local,
        urlQueryComponent,
        lightbox
    }

    useEffect(() => {
        const writeTimestampToSessionStorage = function (key, overrideExisting = false) {
            let timestamp = sessionStorage.getItem(key);
            const newTimestamp = String(Date.now())
            if (!timestamp || overrideExisting) {
                timestamp = newTimestamp
            }
            sessionStorage.setItem(key, timestamp)
        }
        writeTimestampToSessionStorage('timestampUserStartedVisit')
    }, [])

    useEffect(() => {
        local.load()
        roomParameters.load()
        help.unsetContext()
    }, [])

    useEffect(() => {
        catalog.load(local.getLang(true))
    }, [local.getLang()])

    if (!roomParameters.isLoaded() || !catalog.isLoaded()) {
        return <AppProvider value={provider}>
            <BaseLayout>
                <MenuTopSimple/>
                <LoadScreen/>
                {modal.hasHelp() && <Help/>}
                <Message/>
            </BaseLayout>
        </AppProvider>
    }

    if (mobil.isMobil() && progress.isRoom())  {
        return <AppProvider value={provider}>
            <BaseLayout>
                <MenuTopSimple/>
                <SmallScreenLook/>
                {modal.hasHelp() && <Help/>}
                <Message/>
            </BaseLayout>
        </AppProvider>
    }

    if (getCodeFromUrl()) {
        return <AppProvider value={provider}>
            <BaseLayout>
                <MenuTopSimple/>
                <LoadRoom/>
                {modal.hasHelp() && <Help/>}
                <Message/>
            </BaseLayout>
        </AppProvider>
    }

    return <AppProvider value={provider}>
        <BaseLayout>
            <MenuTop/>
            <MenuBottom/>
            <Canvas/>
            {progress.isRoom() && <RoomType/>}
            {progress.isShape() && <Shape/>}
            {progress.isWall() && <Wall/>}
            {progress.isFloor() && <Floor/>}
            {progress.isCeiling() && <Ceiling/>}
            {progress.isNoise() && <Noise/>}
            {progress.isConditions() && <Conditions/>}
            {progress.isSolution() && <Solution setProduct={setProduct}/>}
            {modal.hasSolutionLoad() && <SolutionRepositoryLoad/>}
            {modal.hasSolutionSave() && <SolutionRepositorySave/>}
            {modal.hasSolutionSuggestions() && <SolutionSuggestions/>}
            {modal.hasAdditionalSolutionSuggestions() && <AdditionalSolutionSuggestions/>}
            {modal.hasSolutionProductAdd() && <SolutionProductAdd setProduct={setProduct}/>}
            {modal.hasSolutionProducts() && <SolutionProducts setProduct={setProduct}/>}
            {modal.hasRequestQuote() && <RequestQuote/>}
            {modal.hasSolutionReview() && <SolutionReview setProduct={setProduct}/>}
            {modal.hasPrivacy() && <PrivacyInfo/>}
            {modal.hasHelp() && <Help/>}
            {lightbox.hasGallery() && <Gallery product={product}/>}
            <Message/>
            <Debug/>
        </BaseLayout>
    </AppProvider>
}
